import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const VendorActivate = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [vendorInfo, setVendorInfo] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No activation token found. Please use the link from your invitation email.');
      setLoading(false);
      return;
    }
    axios.get(`${API}/api/vendor-portal/activate?token=${token}`)
      .then(res => {
        setVendorInfo(res.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Invalid or expired activation link.');
        setLoading(false);
      });
  }, [token]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setActivating(true);
    try {
      await axios.post(`${API}/api/vendor-portal/activate`, { token, password });
      setActivated(true);
      toast.success('Account activated! Redirecting to login...');
      setTimeout(() => navigate('/vendor/login'), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed');
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}>
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Lock className="h-7 w-7 text-white" />
          </div>
          <CardTitle className="text-xl">Vendor Portal Activation</CardTitle>
          <p className="text-sm text-gray-500">APC EstateFlow</p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-gray-500 py-8">Validating activation link...</p>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-600 font-medium">{error}</p>
              <Button className="mt-4" variant="outline" onClick={() => navigate('/vendor/login')}>
                Go to Vendor Login
              </Button>
            </div>
          ) : activated ? (
            <div className="text-center py-8">
              <CheckCircle className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-emerald-700">Account Activated!</h3>
              <p className="text-gray-600 text-sm mt-2">Your vendor portal account is ready. Redirecting to login...</p>
              <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/vendor/login')}>
                Go to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleActivate} className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm">
                <p className="font-medium text-emerald-800">Welcome, {vendorInfo?.contact_name || 'Vendor'}!</p>
                <p className="text-emerald-700 mt-1">
                  <strong>{vendorInfo?.company_name}</strong> has been set up in APC EstateFlow.
                  Create your password below to access the Vendor Portal.
                </p>
                <p className="text-emerald-600 mt-1 text-xs">You do not need to register again — your vendor profile is already created.</p>
              </div>
              <div>
                <Label>Email</Label>
                <Input value={vendorInfo?.email || ''} disabled className="bg-gray-50" />
              </div>
              <div>
                <Label>Create Password *</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters" required data-testid="activation-password" />
              </div>
              <div>
                <Label>Confirm Password *</Label>
                <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password" required data-testid="activation-confirm-password" />
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={activating}
                data-testid="activate-account-btn">
                {activating ? 'Activating...' : 'Activate Account'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorActivate;
